/**
 * DatabaseView Component
 * Full database view with gallery, kanban, list views
 * Notion/AppFlowy-inspired design
 */

import React, { useState, useMemo } from 'react';
import { 
  Box, Text, Group, ActionIcon, Menu, Stack, 
  TextInput, Button, Modal, Select, Badge,
  SegmentedControl, SimpleGrid, Divider
} from '@mantine/core';
import { 
  IconPlus, IconLayoutGrid, IconLayoutList, IconColumns, 
  IconCalendar, IconFilter, IconSortAscending, IconSettings,
  IconDotsVertical, IconPencil, IconTrash, IconCopy, IconGripVertical
} from '@tabler/icons-react';
import { 
  DatabaseSchema, 
  DatabaseItem, 
  ViewConfig, 
  ViewType,
  CardDisplayConfig,
  createDefaultCardDisplay
} from '../../types/database/item';
import { PropertyDefinition, PropertyType, SelectOption } from '../../types/database/property';
import { GalleryCard, QuickAddCard } from '../database/GalleryCard';
import { createPropertyDefinition, DEFAULT_STATUS_OPTIONS } from '../../utils/propertyUtils';

// Sample data for demo
const SAMPLE_PROPERTIES: PropertyDefinition[] = [
  { id: 'prop_title', name: 'Name', type: 'title', visible: true, position: 0 },
  { id: 'prop_status', name: 'Status', type: 'status', visible: true, position: 1, statusOptions: DEFAULT_STATUS_OPTIONS },
  { id: 'prop_priority', name: 'Priority', type: 'select', visible: true, position: 2, options: [
    { id: 'p1', name: 'High', color: 'red' },
    { id: 'p2', name: 'Medium', color: 'yellow' },
    { id: 'p3', name: 'Low', color: 'green' },
  ]},
  { id: 'prop_due', name: 'Due Date', type: 'date', visible: true, position: 3 },
  { id: 'prop_assignee', name: 'Assignee', type: 'person', visible: true, position: 4 },
];

const SAMPLE_ITEMS: DatabaseItem[] = [
  {
    id: 'item_1',
    databaseId: 'db_1',
    title: 'Design Landing Page',
    icon: '🎨',
    cover: { type: 'image', value: 'https://picsum.photos/seed/design/400/200' },
    properties: [
      { propertyId: 'prop_status', value: 'In Progress' },
      { propertyId: 'prop_priority', value: 'High' },
      { propertyId: 'prop_due', value: '2025-01-15' },
    ],
    createdAt: new Date('2025-01-01'),
    updatedAt: new Date('2025-01-10'),
  },
  {
    id: 'item_2',
    databaseId: 'db_1',
    title: 'Implement Auth System',
    icon: '🔐',
    cover: { type: 'color', value: '#6366f1' },
    properties: [
      { propertyId: 'prop_status', value: 'To Do' },
      { propertyId: 'prop_priority', value: 'High' },
      { propertyId: 'prop_due', value: '2025-01-20' },
    ],
    createdAt: new Date('2025-01-02'),
    updatedAt: new Date('2025-01-08'),
  },
  {
    id: 'item_3',
    databaseId: 'db_1',
    title: 'Write Documentation',
    icon: '📝',
    cover: { type: 'gradient', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
    properties: [
      { propertyId: 'prop_status', value: 'Done' },
      { propertyId: 'prop_priority', value: 'Low' },
      { propertyId: 'prop_due', value: '2025-01-05' },
    ],
    createdAt: new Date('2024-12-28'),
    updatedAt: new Date('2025-01-05'),
  },
  {
    id: 'item_4',
    databaseId: 'db_1',
    title: 'Setup CI/CD Pipeline',
    icon: '⚙️',
    cover: { type: 'image', value: 'https://picsum.photos/seed/pipeline/400/200' },
    properties: [
      { propertyId: 'prop_status', value: 'To Do' },
      { propertyId: 'prop_priority', value: 'Medium' },
      { propertyId: 'prop_due', value: '2025-01-25' },
    ],
    createdAt: new Date('2025-01-03'),
    updatedAt: new Date('2025-01-03'),
  },
  {
    id: 'item_5',
    databaseId: 'db_1',
    title: 'User Testing Sessions',
    icon: '👥',
    properties: [
      { propertyId: 'prop_status', value: 'In Progress' },
      { propertyId: 'prop_priority', value: 'Medium' },
    ],
    createdAt: new Date('2025-01-04'),
    updatedAt: new Date('2025-01-09'),
  },
  {
    id: 'item_6',
    databaseId: 'db_1',
    title: 'Performance Optimization',
    icon: '⚡',
    cover: { type: 'color', value: '#10b981' },
    properties: [
      { propertyId: 'prop_status', value: 'Done' },
      { propertyId: 'prop_priority', value: 'High' },
    ],
    createdAt: new Date('2024-12-20'),
    updatedAt: new Date('2025-01-02'),
  },
];

// Default card display config
const DEFAULT_CARD_DISPLAY: CardDisplayConfig = {
  showCover: true,
  cardSize: 'medium',
  visiblePropertyIds: ['prop_status', 'prop_priority'],
  showPageIcon: true,
  showDescription: false,
  showTimestamps: true,
};

export const DatabaseView: React.FC = () => {
  // State
  const [viewType, setViewType] = useState<ViewType>('gallery');
  const [cardSize, setCardSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [showCover, setShowCover] = useState(true);
  const [items, setItems] = useState<DatabaseItem[]>(SAMPLE_ITEMS);
  const [likedItems, setLikedItems] = useState<Set<string>>(new Set());
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  
  // Card display config
  const cardDisplayConfig: CardDisplayConfig = {
    ...DEFAULT_CARD_DISPLAY,
    cardSize,
    showCover,
    visiblePropertyIds: ['prop_status', 'prop_priority', 'prop_due'],
  };
  
  // Handle add new item
  const handleAddItem = () => {
    if (!newItemTitle.trim()) return;
    
    const newItem: DatabaseItem = {
      id: `item_${Date.now()}`,
      databaseId: 'db_1',
      title: newItemTitle,
      properties: selectedStatus 
        ? [{ propertyId: 'prop_status', value: selectedStatus }]
        : [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    setItems([newItem, ...items]);
    setNewItemTitle('');
    setSelectedStatus(null);
    setAddModalOpen(false);
  };
  
  // Toggle like
  const toggleLike = (itemId: string) => {
    setLikedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };
  
  // Group items for kanban view
  const groupedItems = useMemo(() => {
    const groups: Record<string, DatabaseItem[]> = {
      'To Do': [],
      'In Progress': [],
      'Done': [],
    };
    
    items.forEach(item => {
      const statusProp = item.properties.find(p => p.propertyId === 'prop_status');
      const status = (statusProp?.value as string) || 'To Do';
      if (groups[status]) {
        groups[status].push(item);
      }
    });
    
    return groups;
  }, [items]);
  
  return (
    <Box className="database-view">
      {/* Database Header */}
      <Box className="database-header" p="md" pb="sm">
        <Group justify="space-between">
          <Group gap="md">
            <Text size="xl" fw={700}>Project Tasks</Text>
            <Badge variant="light" size="lg">{items.length} items</Badge>
          </Group>
          
          <Group gap="sm">
            {/* View Type Selector */}
            <SegmentedControl
              size="xs"
              value={viewType}
              onChange={(v) => setViewType(v as ViewType)}
              data={[
                { label: <IconLayoutGrid size={14} />, value: 'gallery' },
                { label: <IconColumns size={14} />, value: 'board' },
                { label: <IconLayoutList size={14} />, value: 'list' },
              ]}
            />
            
            {/* Card Size Toggle */}
            <Menu shadow="md" width={200}>
              <Menu.Target>
                <ActionIcon variant="subtle" size="lg">
                  <IconSettings size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>Card Size</Menu.Label>
                <Menu.Item onClick={() => setCardSize('small')}>Small</Menu.Item>
                <Menu.Item onClick={() => setCardSize('medium')}>Medium</Menu.Item>
                <Menu.Item onClick={() => setCardSize('large')}>Large</Menu.Item>
                <Divider my="xs" />
                <Menu.Item 
                  onClick={() => setShowCover(!showCover)}
                  rightSection={showCover ? '✓' : ''}
                >
                  Show Cover
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            
            {/* Add Button */}
            <Button 
              leftSection={<IconPlus size={16} />}
              variant="filled"
              onClick={() => setAddModalOpen(true)}
            >
              New
            </Button>
          </Group>
        </Group>
      </Box>
      
      {/* View Content */}
      <Box className="database-content" p="md" pt="xs">
        {viewType === 'gallery' && (
          <Box className="gallery-view">
            <Group gap="md" align="stretch">
              {/* Gallery Grid */}
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="md">
                {/* Quick Add Card */}
                <QuickAddCard onClick={() => setAddModalOpen(true)} />
                
                {/* Item Cards */}
                {items.map(item => (
                  <GalleryCard
                    key={item.id}
                    item={item}
                    displayConfig={cardDisplayConfig}
                    properties={SAMPLE_PROPERTIES}
                    onClick={() => console.log('Open item:', item.id)}
                    onLike={() => toggleLike(item.id)}
                    liked={likedItems.has(item.id)}
                  />
                ))}
              </SimpleGrid>
            </Group>
          </Box>
        )}
        
        {viewType === 'board' && (
          <Box className="kanban-view">
            <Group gap="md" align="flex-start" wrap="nowrap" style={{ overflowX: 'auto' }}>
              {Object.entries(groupedItems).map(([status, statusItems]) => (
                <Box key={status} className="kanban-column" style={{ minWidth: 280 }}>
                  <Box className="kanban-column-header" p="sm" mb="sm">
                    <Group justify="space-between">
                      <Group gap="xs">
                        <Text fw={600} size="sm">{status}</Text>
                        <Badge size="sm" variant="light">{statusItems.length}</Badge>
                      </Group>
                      <ActionIcon variant="subtle" size="xs">
                        <IconPlus size={14} />
                      </ActionIcon>
                    </Group>
                  </Box>
                  
                  <Stack gap="sm">
                    {statusItems.map(item => (
                      <Box key={item.id} className="kanban-card">
                        {item.cover && item.cover.type === 'image' && (
                          <Image
                            src={item.cover.value}
                            h={80}
                            fit="cover"
                            radius="sm"
                          />
                        )}
                        <Box p="sm">
                          <Group gap="xs" mb="xs">
                            {item.icon && <Text size="md">{item.icon}</Text>}
                            <Text size="sm" fw={500} className="truncate">
                              {item.title}
                            </Text>
                          </Group>
                          {item.cover?.type === 'color' && (
                            <Box 
                              h={4} 
                              style={{ 
                                backgroundColor: item.cover.value,
                                borderRadius: 4,
                              }} 
                            />
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Group>
          </Box>
        )}
        
        {viewType === 'list' && (
          <Box className="list-view">
            {/* List Header */}
            <Group p="sm" className="list-header" mb="xs">
              <Text size="xs" c="dimmed" w={30}>#</Text>
              <Text size="xs" c="dimmed" fw={600}>Name</Text>
              <Text size="xs" c="dimmed" fw={600}>Status</Text>
              <Text size="xs" c="dimmed" fw={600}>Priority</Text>
              <Text size="xs" c="dimmed" fw={600}>Due Date</Text>
              <Box w={40} />
            </Group>
            
            {/* List Rows */}
            {items.map((item, index) => {
              const statusProp = item.properties.find(p => p.propertyId === 'prop_status');
              const priorityProp = item.properties.find(p => p.propertyId === 'prop_priority');
              const dateProp = item.properties.find(p => p.propertyId === 'prop_due');
              
              return (
                <Group 
                  key={item.id} 
                  p="sm" 
                  className="list-row"
                  onClick={() => console.log('Open item:', item.id)}
                >
                  <Text size="sm" c="dimmed" w={30}>{index + 1}</Text>
                  <Group gap="xs" style={{ flex: 1 }}>
                    {item.icon && <Text>{item.icon}</Text>}
                    <Text size="sm" fw={500}>{item.title}</Text>
                  </Group>
                  <Badge 
                    size="sm" 
                    variant="light"
                    color={
                      statusProp?.value === 'Done' ? 'green' :
                      statusProp?.value === 'In Progress' ? 'blue' : 'gray'
                    }
                  >
                    {statusProp?.value as string || 'To Do'}
                  </Badge>
                  <Badge size="sm" variant="light" color={
                    priorityProp?.value === 'High' ? 'red' :
                    priorityProp?.value === 'Medium' ? 'yellow' : 'green'
                  }>
                    {priorityProp?.value as string || 'Low'}
                  </Badge>
                  <Text size="sm" c="dimmed">
                    {dateProp?.value 
                      ? new Date(dateProp.value as string).toLocaleDateString()
                      : '-'}
                  </Text>
                  <Menu shadow="md" width={150}>
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="sm">
                        <IconDotsVertical size={14} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item leftSection={<IconPencil size={14} />}>Edit</Menu.Item>
                      <Menu.Item leftSection={<IconCopy size={14} />}>Duplicate</Menu.Item>
                      <Menu.Divider />
                      <Menu.Item color="red" leftSection={<IconTrash size={14} />}>
                        Delete
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Group>
              );
            })}
          </Box>
        )}
      </Box>
      
      {/* Add Item Modal */}
      <Modal 
        opened={addModalOpen} 
        onClose={() => setAddModalOpen(false)}
        title="Add New Item"
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Title"
            placeholder="Enter item title"
            value={newItemTitle}
            onChange={(e) => setNewItemTitle(e.currentTarget.value)}
            autoFocus
          />
          
          <Select
            label="Status"
            placeholder="Select status"
            data={['To Do', 'In Progress', 'Done']}
            value={selectedStatus}
            onChange={setSelectedStatus}
          />
          
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => setAddModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddItem} disabled={!newItemTitle.trim()}>
              Add Item
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};

export default DatabaseView;